import { parseNumberish } from '@/api/keywordMetrics';
import {
  EMPTY_RAW_METRICS,
  type MetricPolarity,
  type RawMetricTotals,
  type WorkspaceMetricColumnDef,
} from '@/utils/workspaceAdProductMetrics';
import { deriveTargetingTypeMetrics } from '@/utils/workspaceTargetingTypeMetrics';

export type ProductAdCatalogRow = {
  adId: string;
  campaignId: string;
  adGroupId: string;
  asin: string;
  sku: string;
  state: string;
  adProduct: 'SP' | 'SD';
  productName: string;
};

export type AdvertisedProductRow = {
  asin: string;
  productName: string;
  imageUrl?: string | null;
  status: string;
  inventory: number | null;
  adMetrics: ReturnType<typeof deriveTargetingTypeMetrics>;
  totalSales: number | null;
  organicSales: number | null;
  tacos: number | null;
};

export type AdProductRow = {
  rowKey: string;
  productName: string;
  asin: string;
  imageUrl?: string | null;
  campaignId: string;
  campaignName: string;
  agAsins: string;
  status: string;
  metrics: ReturnType<typeof deriveTargetingTypeMetrics>;
};

export type AdvertisedProductColumnDef = {
  key: string;
  label: string;
  polarity?: MetricPolarity;
  format?: WorkspaceMetricColumnDef['format'];
};

export const ADVERTISED_PRODUCT_METRIC_KEYS = [
  'impressions',
  'clicks',
  'ctr',
  'cost',
  'cpc',
  'cr',
  'purchases30d',
  'sales30d',
] as const;

export function deriveProductAdMetrics(raw: RawMetricTotals) {
  return deriveTargetingTypeMetrics(raw);
}

export function emptyProductMetrics() {
  return deriveTargetingTypeMetrics({ ...EMPTY_RAW_METRICS });
}

const STATUS_ROLLUP_ORDER = ['ENABLED', 'PAUSED', 'ARCHIVED'] as const;

export function statusRollup(states: string[]): string {
  const normalized = states.map((s) => s.trim().toUpperCase()).filter(Boolean);
  if (normalized.length === 0) return '—';
  const unique = [...new Set(normalized)];
  unique.sort((left, right) => {
    const leftRank = STATUS_ROLLUP_ORDER.indexOf(left as (typeof STATUS_ROLLUP_ORDER)[number]);
    const rightRank = STATUS_ROLLUP_ORDER.indexOf(right as (typeof STATUS_ROLLUP_ORDER)[number]);
    const leftOrder = leftRank === -1 ? STATUS_ROLLUP_ORDER.length : leftRank;
    const rightOrder = rightRank === -1 ? STATUS_ROLLUP_ORDER.length : rightRank;
    return leftOrder - rightOrder || left.localeCompare(right);
  });
  return unique.join(', ');
}

export function buildAdvertisedProductRows(
  catalog: ProductAdCatalogRow[],
  metricsByAsin: Map<string, RawMetricTotals>,
  scopedCampaignIds: Set<string> | null
): AdvertisedProductRow[] {
  const byAsin = new Map<
    string,
    { names: string[]; states: string[]; asins: string[] }
  >();

  for (const row of catalog) {
    if (scopedCampaignIds && !scopedCampaignIds.has(row.campaignId)) continue;
    const asin = row.asin || row.sku || row.adId;
    if (!asin) continue;
    const bucket = byAsin.get(asin) ?? { names: [], states: [], asins: [] };
    if (row.productName) bucket.names.push(row.productName);
    if (row.state) bucket.states.push(row.state);
    bucket.asins.push(asin);
    byAsin.set(asin, bucket);
  }

  const out: AdvertisedProductRow[] = [];
  for (const [asin, meta] of byAsin) {
    const raw = metricsByAsin.get(asin) ?? { ...EMPTY_RAW_METRICS };
    out.push({
      asin,
      productName: meta.names[0] || asin,
      imageUrl: null,
      status: statusRollup(meta.states),
      inventory: null,
      adMetrics: deriveProductAdMetrics(raw),
      totalSales: null,
      organicSales: null,
      tacos: null,
    });
  }

  out.sort((a, b) => b.adMetrics.cost - a.adMetrics.cost || a.productName.localeCompare(b.productName));
  return out;
}

export function buildAdProductRows(
  catalog: ProductAdCatalogRow[],
  metricsByAdKey: Map<string, RawMetricTotals>,
  campaignNameById: Map<string, string>,
  asinsByAdGroupId: Map<string, string[]>,
  scopedCampaignIds: Set<string> | null
): AdProductRow[] {
  const out: AdProductRow[] = [];
  for (const row of catalog) {
    if (scopedCampaignIds && !scopedCampaignIds.has(row.campaignId)) continue;
    const key = `${row.adProduct}|${row.adId}`;
    const raw = metricsByAdKey.get(key) ?? { ...EMPTY_RAW_METRICS };
    const agAsins = (asinsByAdGroupId.get(row.adGroupId) ?? []).join(', ') || row.asin || '—';
    out.push({
      rowKey: key,
      productName: row.productName || row.asin || row.sku || row.adId,
      asin: row.asin,
      imageUrl: null,
      campaignId: row.campaignId,
      campaignName: campaignNameById.get(row.campaignId) || row.campaignId,
      agAsins,
      status: row.state || '—',
      metrics: deriveProductAdMetrics(raw),
    });
  }
  out.sort((a, b) => b.metrics.cost - a.metrics.cost || a.productName.localeCompare(b.productName));
  return out;
}

export function parseSpProductAdsPayload(data: unknown): ProductAdCatalogRow[] {
  if (!data || typeof data !== 'object') return [];
  const raw = data as Record<string, unknown>;
  const items = Array.isArray(raw.product_ads) ? raw.product_ads : [];
  const out: ProductAdCatalogRow[] = [];
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const adId = row.ad_id != null ? String(row.ad_id) : row.adId != null ? String(row.adId) : '';
    if (!adId) continue;
    out.push({
      adId,
      campaignId:
        row.campaign_id != null
          ? String(row.campaign_id)
          : row.campaignId != null
            ? String(row.campaignId)
            : '',
      adGroupId:
        row.ad_group_id != null
          ? String(row.ad_group_id)
          : row.adGroupId != null
            ? String(row.adGroupId)
            : '',
      asin: row.asin != null ? String(row.asin) : '',
      sku: row.sku != null ? String(row.sku) : '',
      state: row.state != null ? String(row.state) : '',
      adProduct: 'SP',
      productName:
        row.asin != null
          ? String(row.asin)
          : row.sku != null
            ? String(row.sku)
            : adId,
    });
  }
  return out;
}

export function parseSdProductAdsPayload(data: unknown): ProductAdCatalogRow[] {
  if (!data || typeof data !== 'object') return [];
  const raw = data as Record<string, unknown>;
  const items = Array.isArray(raw.product_ads) ? raw.product_ads : [];
  const out: ProductAdCatalogRow[] = [];
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const adId = row.ad_id != null ? String(row.ad_id) : row.adId != null ? String(row.adId) : '';
    if (!adId) continue;
    out.push({
      adId,
      campaignId:
        row.campaign_id != null
          ? String(row.campaign_id)
          : row.campaignId != null
            ? String(row.campaignId)
            : '',
      adGroupId:
        row.ad_group_id != null
          ? String(row.ad_group_id)
          : row.adGroupId != null
            ? String(row.adGroupId)
            : '',
      asin: row.asin != null ? String(row.asin) : '',
      sku: row.sku != null ? String(row.sku) : '',
      state: row.state != null ? String(row.state) : '',
      adProduct: 'SD',
      productName:
        row.asin != null
          ? String(row.asin)
          : row.sku != null
            ? String(row.sku)
            : adId,
    });
  }
  return out;
}

export function accumulateProductMetrics(
  map: Map<string, RawMetricTotals>,
  key: string,
  row: { impressions?: number; clicks?: number; cost?: number; purchases30d?: number; sales30d?: number }
) {
  const existing = map.get(key) ?? { ...EMPTY_RAW_METRICS };
  existing.impressions += parseNumberish(row.impressions) ?? 0;
  existing.clicks += parseNumberish(row.clicks) ?? 0;
  existing.cost += parseNumberish(row.cost) ?? 0;
  existing.purchases30d += parseNumberish(row.purchases30d) ?? 0;
  existing.sales30d += parseNumberish(row.sales30d) ?? 0;
  map.set(key, existing);
}

export function buildAsinsByAdGroup(catalog: ProductAdCatalogRow[]): Map<string, string[]> {
  const map = new Map<string, Set<string>>();
  for (const row of catalog) {
    if (!row.adGroupId || !row.asin) continue;
    const set = map.get(row.adGroupId) ?? new Set<string>();
    set.add(row.asin);
    map.set(row.adGroupId, set);
  }
  const out = new Map<string, string[]>();
  for (const [id, set] of map) {
    out.set(id, [...set].sort());
  }
  return out;
}
